import { useState } from 'react';

import { useMasterPosts } from '@/utils/hooks/scheduler/calendar/useMasterPosts';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { deleteApi } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { SchedulerPostType } from '@/utils/hooks/context/scheduler/types';

import { SCHEDULER_MASTER_POSTS, SCHEDULER_POST } from '@/lib/routes/apiEndpoints';

import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
import Modal from '@/components/base/Modal';
import { FooterCrossPost } from '@/components/pages/scheduler/deletePostModal/footer/FooterCrossPost';
import { FooterSinglePost } from '@/components/pages/scheduler/deletePostModal/footer/FooterSinglePost';
import { Trans } from '@/components/tools/translation';

type ContentProps = {
    post: SchedulerPostType;
    toggle: () => void;
    deleteSuccess: () => void;
};
type FormType = {
    isDeletingSinglePost: boolean;
    isDeletingMultiPost: boolean;
    error?: string;
};

export const Content = ({ post, toggle, deleteSuccess }: ContentProps) => {
    const { t } = useTranslation('scheduler');
    const [formState, setFormState] = useState<FormType>({
        isDeletingSinglePost: false,
        isDeletingMultiPost: false,
        error: null
    });
    const { masterPosts } = useMasterPosts({ masterIds: [post?.master_id] });

    const onDelete = async (isDeletingMultiPost = false) => {
        setFormState({
            isDeletingSinglePost: !isDeletingMultiPost,
            isDeletingMultiPost: isDeletingMultiPost,
            error: null
        });
        try {
            if (isDeletingMultiPost) {
                await deleteApi(
                    injectEndpointUrlParams(SCHEDULER_MASTER_POSTS, {
                        masterId: post?.master_id
                    })
                );
            } else {
                await deleteApi(
                    injectEndpointUrlParams(SCHEDULER_POST, {
                        SPID: post?.social_profile_id,
                        postId: post?.id
                    })
                );
            }
            deleteSuccess();
            toggle();
        } catch (e) {
            setFormState({
                isDeletingSinglePost: false,
                isDeletingMultiPost: false,
                error: t('errors:error_occurred_delete_data')
            });
        }
    };

    return (
        <Modal.Body className="px-0 pt-12">
            <div className="px-5 sm:px-12">
                {formState.error ? (
                    <CompoundBanner type="error" className="mb-4" title={t('errors:error')}>
                        <CompoundBanner.Content title={t('errors:error')}>
                            <p>{formState.error}</p>
                        </CompoundBanner.Content>
                        <CompoundBanner.Close
                            onClose={() => {
                                setFormState((prevState) => ({
                                    ...prevState,
                                    error: null
                                }));
                            }}
                        />
                    </CompoundBanner>
                ) : null}
                <Modal.Title>{t('modal_delete_post_title')}</Modal.Title>
                <Modal.Content className="pt-m text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                    <p>{t('modal_delete_post_sentence')}</p>
                    {post?.master_id ? (
                        <p className="mt-2 text-xs">
                            <Trans
                                i18nKey="scheduler:modal_delete_post_note_multi_post"
                                components={{
                                    br: <br />,
                                    italic: <span className="italic" />
                                }}
                            />
                        </p>
                    ) : null}
                </Modal.Content>
            </div>
            {post?.master_id ? (
                <FooterCrossPost
                    toggle={toggle}
                    onDelete={onDelete}
                    isDeletingCrossPost={formState?.isDeletingMultiPost ?? false}
                    isDeletingSinglePost={formState?.isDeletingSinglePost ?? false}
                    masterPostIsReadOnly={masterPosts?.[0]?.read_only}
                />
            ) : (
                <FooterSinglePost
                    toggle={toggle}
                    onDelete={onDelete}
                    isDeletingSinglePost={formState?.isDeletingSinglePost ?? false}
                />
            )}
        </Modal.Body>
    );
};
